import { Component, ElementRef, inject, input, effect, untracked } from "@angular/core";
import { CurrencyPipe, DecimalPipe } from "@angular/common";
import { toSignal } from "@angular/core/rxjs-interop";
import * as d3 from "d3";
import { map } from "rxjs";

import { MacroNumberPipe } from "@vault/web-vault/app/pipes/macro-number.pipe";
import { UserStoreService } from "@vault/web-vault/app/services/store/user/user.store.service";
import { ThemeType } from "@bitwarden/common/enums/themeType";
import { AbstractThemingService } from "@vault/angular/services/theming/theming.service.abstraction";

// Global, unencapsulated stylesheet: injected via this side-effect import, not Angular's
// styleUrls/styleUrl, so it isn't scoped to this component.
import "./bubble-chart.component.scss";
import {
  bubbleDriftStart,
  bubbleFill,
  bubbleLabel,
  bubbleShare,
  computeBubbleLayout,
} from "./bubble-chart.utils";
import { AsterisksPipe } from "@vault/web-vault/app/pipes/asterisks.pipe";
import { prefersReducedMotion } from "@vault/web-vault/app/shared/utils/reduced-motion.util";

interface BubbleData {
  text: string;
  amount: number;
}

interface NodeType extends BubbleData {
  radius: number;
  x: number;
  y: number;
}

// text classes
const SHARE = "share";
const TITLE = "title";
const SUBTITLE = "subtitle";

// tooltip labels
const ACCOUNT_NAME = "Account Name:";
const BALANCE = "Balance:";

//max bubble should be in the bubble chart
const MAX_BUBBLES = 10;

const OTHER_ACCOUNT = "other";

const GREEN: string = "#00C4C4";
const LIGHT_GREEN: string = "#C8F2F2"; // teal.100 — gradient is light-mode only, left un-themed until dark treatment
const RED_ORANGE: string = "#D23E00";

// Top stop of the dark-mode stroke gradient. #363A3F = --color-border-subtle (dark).
// Bottom stop uses --bubble-stroke-teal (hand-picked; --color-accent-today has no dark override).
const DARK_SURFACE_GRAY = "#363A3F";
// 100% stop of the money-out radial gradient.
const RED_TINT = "#fecaca";

@Component({
  selector: "app-bubble-chart",
  templateUrl: "./bubble-chart.component.html",
  providers: [MacroNumberPipe, DecimalPipe, CurrencyPipe, AsterisksPipe],
})
export class BubbleChartComponent {
  dataSet = input<BubbleData[]>([]);
  showBalance = input<boolean>(false);
  protected elementRef = inject(ElementRef);
  protected userStore = inject(UserStoreService);
  protected macroNumberPipe = inject(MacroNumberPipe);
  protected currencyPipe = inject(CurrencyPipe);
  protected asterisksPipe = inject(AsterisksPipe);
  protected symbol: string; // use base currency symbol
  protected tooltips: any;
  // Amounts of the bubbles on screen, which each share is taken of.
  private shownAmounts: number[] = [];

  width = 450;
  height = 350;

  private readonly themingService = inject(AbstractThemingService);
  // Reactive to both explicit theme changes and OS-level dark/light switches (via theme$.effectiveTheme).
  // theme$ is `_theme.pipe(filter(x => x !== null))` over a BehaviorSubject, so subscribing replays
  // the current theme synchronously — but only once bootstrap has pushed one, which it always has by
  // the time this chart renders. initialValue: false covers the pre-bootstrap/test case.
  private readonly isDarkMode = toSignal(
    this.themingService.theme$.pipe(map((t) => t.effectiveTheme === ThemeType.Dark)),
    { initialValue: false },
  );

  constructor() {
    effect(() => {
      if (this.dataSet() && this.dataSet().length > 0 && this.isDataSetDrawable(this.dataSet())) {
        this.symbol = this.userStore.preferences.preferenceView()?.coreCurrency ?? "USD";
        const sortedData = [...this.dataSet()].sort(
          (prev, cur) => Math.abs(cur.amount) - Math.abs(prev.amount),
        );
        this.drawChart(this.applyDataSetRules(sortedData, MAX_BUBBLES));
      }
    });

    // Re-mask (or unmask) the amounts and shares whenever the balance is hidden or shown. The
    // labels are laid out again too: the block is centred by its widest line, which changes.
    effect(() => {
      this.showBalance();
      d3.select(this.elementRef.nativeElement)
        .selectAll<SVGTextElement, NodeType>("g.bubble text")
        .each((d, i, texts) => this.renderLabel(texts[i], d));
    });
  }

  isDataSetDrawable(dataSets: BubbleData[]) {
    return dataSets.every((dataSet: BubbleData) => Object.values(dataSet).every((value) => value));
  }

  drawChart(dataSet: BubbleData[]) {
    const canvasSelection = d3.select(this.elementRef.nativeElement).select("#bubble-chart");
    canvasSelection.select("svg").remove();

    const canvas = canvasSelection
      .append("svg")
      .attr("viewBox", `0 0 ${this.width} ${this.height}`)
      .attr("width", "100%")
      .attr("height", "100%");

    const defs = canvas.append("defs");

    // Area follows the amount; money-in sits on the divider from above, money-out from below. The
    // divider only places the bubbles: no line is drawn for it.
    const amounts = dataSet.map((d) => d.amount);
    this.shownAmounts = amounts;
    const layout = computeBubbleLayout(amounts, this.width, this.height);
    const { radii } = layout;
    const nodes: NodeType[] = dataSet.map((d, i) => ({
      ...d,
      radius: radii[i],
      x: layout.x[i],
      y: layout.y[i],
    }));
    const dark = this.isDarkMode();
    const motion = !prefersReducedMotion();

    nodes.forEach((data, index) =>
      this.createStrokeGradient({
        defs,
        index,
        color: data.amount >= 0 ? GREEN : RED_ORANGE,
        dark,
      }),
    );

    this.createBoxShadow(defs);
    this.createGlassyGradients(defs);
    if (!dark) {
      this.createFrostedGlassFilter(defs);
    }

    // Create bubbles
    const bubbles = canvas
      .selectAll(".bubble")
      .data(nodes)
      .join("g")
      .attr("class", "bubble")
      .attr("transform", (d: NodeType) => {
        const start = motion ? bubbleDriftStart(d.x, d.y, d.amount, this.width) : d;
        return `translate(${start.x},${start.y})`;
      })
      .attr("opacity", 0)
      .on("click", (event: MouseEvent, d: any) => {
        this.changeStrokeColor(event, nodes);
        // Canvas coordinates: the bubble's own transform is still moving while it drifts in.
        const [x, y] = d3.pointer(event, canvas.node());
        this.createToolTips(canvas, d, { x, y });
      })
      // mouseleave, not mouseout: moving between the circle and its label stays inside the bubble.
      .on("mouseleave", (event: MouseEvent, d: any) => {
        this.changeStrokeColor(event, nodes, dark ? "none" : "rgba(255, 255, 255, 0.6)");
        this.closeToolTips();
      });

    bubbles
      .append("circle")
      .attr("class", "glow")
      .attr("r", (d) => d.radius * 1.15)
      .attr("fill", (d) => bubbleFill(d.amount, dark))
      .style("filter", "blur(12px)")
      .style("opacity", dark ? 0.2 : 0.6)
      .style("pointer-events", "none");

    bubbles
      .append("circle")
      .attr("class", "glass")
      .attr("r", (d) => d.radius)
      .attr("fill", (d) => bubbleFill(d.amount, dark))
      .attr("stroke", dark ? "none" : "rgba(255, 255, 255, 0.6)")
      .attr("stroke-width", dark ? 0 : 1.5)
      .style("filter", dark ? "none" : "url(#frosted-glass)");

    bubbles
      .append("text")
      .style("cursor", "default")
      .each((d: NodeType, i, texts) => this.renderLabel(texts[i], d));

    // Staggered fade in, drifting from nearer the middle down onto (or up against) the divider.
    bubbles
      .transition()
      .duration(motion ? 1200 : 1000)
      .delay((d, i) => i * 150)
      .ease(d3.easeCubicOut)
      .attr("transform", (d: NodeType) => `translate(${d.x},${d.y})`)
      .attr("opacity", 1);
  }

  // Writes a bubble's label lines into its <text>, replacing any already there. Untracked: the
  // balance toggle re-renders the labels (the effect above), not a full redraw that would replay
  // the entrance.
  renderLabel(textNode: SVGTextElement, d: NodeType) {
    const label = untracked(() =>
      bubbleLabel(d.radius, d.text, this.renderShare(d), this.renderSubtitles(d)),
    );
    const text = d3.select(textNode).attr("text-anchor", label.anchor);
    text.selectAll("tspan").remove();
    for (const line of label.lines) {
      const tspan = text
        .append("tspan")
        .attr("x", label.x)
        .attr("y", line.y)
        .attr("class", { share: SHARE, name: TITLE, amount: SUBTITLE }[line.kind])
        .style("font-size", `${line.fontSize}px`)
        .text(line.text);
      if (line.kind === "amount") {
        tspan.style(
          "fill",
          d.amount >= 0 ? "var(--color-money-in-text)" : "var(--color-money-out-text)",
        );
      }
    }
  }

  // A share reveals how the balances compare, so it is hidden along with them.
  renderShare(d: NodeType) {
    return this.asterisksPipe.transform(bubbleShare(d.amount, this.shownAmounts));
  }

  renderSubtitles(d: NodeType) {
    const value = this.macroNumberPipe.transform(d.amount, this.symbol);
    return this.asterisksPipe.transform(value);
  }

  createToolTips(d3Comp: any, data: NodeType, mouseCoord: { x: number; y: number }) {
    this.closeToolTips();
    const padding = 12;
    const rectHeight = 1.2 * 3 * 18 + padding * 2;
    const rectWidth = this.width / 3;
    this.tooltips = d3Comp
      .append("g")
      // Opens under the pointer, so it must not take the pointer: moving onto it would leave the
      // bubble (mouseleave) and close it.
      .style("pointer-events", "none")
      .attr("transform", this.tooltipsLocation(rectHeight, rectWidth, mouseCoord));

    this.tooltips
      .append("rect")
      .attr("height", rectHeight)
      .attr("width", rectWidth)
      .attr("rx", 5)
      .attr("ry", 5)
      .attr("class", "tooltips");

    const tooltipsT = this.tooltips
      .append("text")
      .attr("x", padding)
      .attr("y", padding)
      .attr("text-anchor", "start");

    tooltipsT

      .append("tspan")
      .attr("x", padding)
      .attr("dy", "1.2em")
      .attr("class", "tooltips-title ")
      .text(ACCOUNT_NAME);

    tooltipsT
      .append("tspan")
      .attr("x", padding)
      .attr("dy", "1.2em")
      .attr("class", "tooltips-text")
      .text(data.text.length > 12 ? data.text.substring(0, rectWidth / 12 + 6) + "..." : data.text);

    tooltipsT
      .append("tspan")
      .attr("x", padding)
      .attr("dy", "1.2em")
      .attr("class", "tooltips-title ")
      .text(BALANCE);

    tooltipsT
      .append("tspan")
      .attr("x", padding)
      .attr("dy", "1.2em")
      .attr("class", "tooltips-text")
      .text(
        this.asterisksPipe.transform(this.currencyPipe.transform(data.amount, this.symbol) ?? ""),
      );
  }

  closeToolTips() {
    // Remove the rectangle and text if they exist
    if (this.tooltips) {
      this.tooltips.remove();
      this.tooltips = null;
    }
  }

  tooltipsLocation(rectHeight: number, rectWidth: number, mouseCoord: { x: number; y: number }) {
    let x = mouseCoord.x;
    let y = mouseCoord.y;
    if (x + rectWidth > this.width) {
      x = x - rectWidth;
    }
    if (y + rectHeight > this.height) {
      y = y - rectHeight;
    }
    return `translate(${x}, ${y})`;
  }

  createBoxShadow(defs: any) {
    const filter = defs
      .append("filter")
      .attr("id", "shadow")
      .attr("x", "-50%")
      .attr("y", "-50%")
      .attr("width", "200%")
      .attr("height", "200%");

    // Append Gaussian Blur
    filter
      .append("feGaussianBlur")
      .attr("in", "SourceAlpha")
      .attr("stdDeviation", 4)
      .attr("result", "blur");

    // Append Offset
    filter
      .append("feOffset")
      .attr("in", "blur")
      .attr("dx", 0)
      .attr("dy", 4)
      .attr("result", "offsetBlur");

    // Append Flood
    filter.append("feFlood").attr("flood-color", "rgba(0, 0, 0, 0.1)");

    // Append Composite
    filter.append("feComposite").attr("in2", "offsetBlur").attr("operator", "in");

    const merge = filter.append("feMerge");
    merge.append("feMergeNode");
    merge.append("feMergeNode").attr("in", "SourceGraphic");
  }

  createGlassyGradients(defs: any) {
    // Money-in (positive) gradient: teal palette matching --color-money-in-text / --color-accent-today.
    const positiveGrad = defs
      .append("radialGradient")
      .attr("id", "positive-grad")
      .attr("cx", "30%")
      .attr("cy", "30%")
      .attr("r", "70%");
    positiveGrad
      .append("stop")
      .attr("offset", "0%")
      .attr("stop-color", "#ffffff")
      .attr("stop-opacity", 0.85);
    positiveGrad
      .append("stop")
      .attr("offset", "50%")
      .attr("stop-color", LIGHT_GREEN)
      .attr("stop-opacity", 0.75);
    positiveGrad
      .append("stop")
      .attr("offset", "100%")
      .attr("stop-color", "#9cdcdc")
      .attr("stop-opacity", 0.65);

    // Money-out (negative) gradient: red palette matching --color-money-out-text.
    const negativeGrad = defs
      .append("radialGradient")
      .attr("id", "negative-grad")
      .attr("cx", "30%")
      .attr("cy", "30%")
      .attr("r", "70%");
    negativeGrad
      .append("stop")
      .attr("offset", "0%")
      .attr("stop-color", "#ffffff")
      .attr("stop-opacity", 0.85);
    negativeGrad
      .append("stop")
      .attr("offset", "50%")
      .attr("stop-color", "#fee2e2")
      .attr("stop-opacity", 0.75);
    negativeGrad
      .append("stop")
      .attr("offset", "100%")
      .attr("stop-color", RED_TINT)
      .attr("stop-opacity", 0.65);
  }

  createFrostedGlassFilter(defs: any) {
    const noiseFilter = defs.append("filter").attr("id", "frosted-glass");
    noiseFilter
      .append("feTurbulence")
      .attr("type", "fractalNoise")
      .attr("baseFrequency", "0.8")
      .attr("numOctaves", "3")
      .attr("result", "noise");
    noiseFilter
      .append("feColorMatrix")
      .attr("type", "matrix")
      .attr("values", "1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 0.15 0")
      .attr("in", "noise")
      .attr("result", "coloredNoise");
    noiseFilter
      .append("feComposite")
      .attr("operator", "in")
      .attr("in", "coloredNoise")
      .attr("in2", "SourceGraphic")
      .attr("result", "compositeNoise");
    noiseFilter
      .append("feBlend")
      .attr("mode", "screen")
      .attr("in", "compositeNoise")
      .attr("in2", "SourceGraphic");
  }

  createStrokeGradient(params: { defs: any; index: number; color: string; dark: boolean }) {
    const { defs, index, color, dark } = params;
    const gradient = defs
      .append("linearGradient")
      .attr("id", `stroke-gradient-${index}`)
      .attr("x1", "0%")
      .attr("y1", "0%")
      .attr("x2", "0%")
      .attr("y2", "100%");

    if (dark) {
      gradient.append("stop").attr("offset", "0%").attr("stop-color", DARK_SURFACE_GRAY);
      gradient
        .append("stop")
        .attr("offset", "100%")
        .style("stop-color", "var(--bubble-stroke-teal)");
    } else {
      gradient.append("stop").attr("offset", "0%").attr("stop-color", color);
      gradient.append("stop").attr("offset", "100%").attr("stop-color", "#FFFFFF");
    }
  }

  changeStrokeColor(event: MouseEvent, nodes: NodeType[], color?: string) {
    const currentCircle = d3.select(event.currentTarget as SVGElement).select(".glass");
    if (color) {
      currentCircle.attr("stroke", color).attr("stroke-width", 1.5);
    } else {
      currentCircle
        .attr("stroke", (d: NodeType) => `url(#stroke-gradient-${nodes.indexOf(d)})`)
        .attr("stroke-width", 2.5);
    }
  }

  // Money-in and money-out bubbles, kept apart so each side gets its own share of MAX_BUBBLES.
  splitBySign(data: BubbleData[]) {
    return {
      positiveBubbles: data.filter((d) => d.amount > 0),
      negativeBubbles: data.filter((d) => d.amount < 0),
    };
  }

  limitBubbles = (bubbles: BubbleData[], limit: number, otherText: string): BubbleData[] => {
    if (bubbles.length <= limit) {
      return [...bubbles];
    }
    const keptBubbles = bubbles.slice(0, limit - 1);
    const remainingBubbles = bubbles.slice(limit - 1);
    const sumOfRemaining = remainingBubbles.reduce((sum, bubble) => sum + bubble.amount, 0);
    return [...keptBubbles, { text: otherText, amount: sumOfRemaining }];
  };

  applyDataSetRules(dataSet: BubbleData[], maxBubbles: number): BubbleData[] {
    const filteredData = dataSet.filter((d) => d.amount !== 0);
    if (filteredData.length <= maxBubbles) {
      return filteredData;
    }
    const { positiveBubbles, negativeBubbles } = this.splitBySign(filteredData);
    const halfMax = Math.floor(maxBubbles / 2);
    const limitedPositiveBubbles = this.limitBubbles(positiveBubbles, halfMax, OTHER_ACCOUNT);
    const limitedNegativeBubbles = this.limitBubbles(negativeBubbles, halfMax, OTHER_ACCOUNT);
    return [...limitedPositiveBubbles, ...limitedNegativeBubbles];
  }
}
